// Capture console output (log/info/warn/error/debug) while preserving original behavior

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

export interface ConsoleEntry {
  id: number
  ts: number
  level: ConsoleLevel
  args: any[]
}

let started = false
let counter = 0
const logs: ConsoleEntry[] = []
const subscribers: Array<(entry: ConsoleEntry) => void> = []

// Keep references to original console methods
const original: Partial<Record<ConsoleLevel, (...args: any[]) => void>> = {}

// Internal queue to batch notifications and avoid re-entrant update loops
let notifyScheduled = false
const pendingForNotify: ConsoleEntry[] = []

export function startConsoleCapture() {
  if (started || typeof window === 'undefined') return
  started = true

  const wrap = (level: ConsoleLevel) => {
    original[level] = console[level].bind(console)
    console[level] = (...args: any[]) => {
      // Call original console first to preserve native behavior and timing
      try { original[level]?.(...args) } catch { /* ignore errors */ }

      // Create entry and store
      const entry: ConsoleEntry = { id: ++counter, ts: Date.now(), level, args }
      logs.push(entry)
      if (logs.length > 1000) logs.shift()

      // Batch subscriber notifications asynchronously to avoid re-entrant render/log loops
      pendingForNotify.push(entry)
      if (!notifyScheduled) {
        notifyScheduled = true
        // Use microtask to keep ordering predictable but avoid sync recursion
        queueMicrotask(() => {
          try {
            // Drain queue snapshot to keep order
            const toSend = pendingForNotify.splice(0, pendingForNotify.length)
            for (const e of toSend) {
              for (const cb of subscribers) {
                try { cb(e) } catch { /* ignore subscriber errors */ }
              }
            }
          } finally {
            notifyScheduled = false
          }
        })
      }
    }
  }

  ;(['log','info','warn','error','debug'] as ConsoleLevel[]).forEach(wrap)
}

export function stopConsoleCapture() {
  if (!started) return
  ;(['log','info','warn','error','debug'] as ConsoleLevel[]).forEach(level => {
    if (original[level]) {
      console[level] = original[level]!
    }
  })
  started = false
}

export function getConsoleLogs(): ConsoleEntry[] {
  return logs.slice()
}

export function clearConsoleLogs() {
  logs.splice(0, logs.length)
}

export function subscribeConsole(cb: (entry: ConsoleEntry) => void) {
  subscribers.push(cb)
  return () => {
    const idx = subscribers.indexOf(cb)
    if (idx >= 0) subscribers.splice(idx, 1)
  }
}
